import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { listApps, previewInject, applyInject } from '../../services/envInjectorApi';
import MultiSelectDropdown from '../shared/MultiSelectDropdown';

const STEPS = ['Configure', 'Preview', 'Results'];

const Badge = ({ color, children }) => {
  const colors = {
    green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    gray: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
};

// Connection string type options matching the backend ConnStringType enum
const CONN_STRING_TYPES = [
  { value: 0, label: 'MySQL' },
  { value: 1, label: 'SQL Server' },
  { value: 2, label: 'SQL Azure' },
  { value: 3, label: 'Custom' },
  { value: 4, label: 'Notification Hub' },
  { value: 5, label: 'Service Bus' },
  { value: 6, label: 'Event Hub' },
  { value: 7, label: 'API Hub' },
  { value: 8, label: 'DocDb' },
  { value: 9, label: 'Redis Cache' },
  { value: 10, label: 'PostgreSQL' },
];

const EnvInjector = () => {
  // ── Step state ─────────────────────────────────────────────
  const [step, setStep] = useState(0); // 0=Configure, 1=Preview, 2=Results

  // ── Setting type tab ───────────────────────────────────────
  const [settingType, setSettingType] = useState(0); // 0=AppSetting, 1=ConnectionString
  const [connectionStringType, setConnectionStringType] = useState(2); // default SqlAzure

  // ── App list ───────────────────────────────────────────────
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [appsError, setAppsError] = useState(null);

  // ── Filters ────────────────────────────────────────────────
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);

  // ── Setting input ──────────────────────────────────────────
  const [settingName, setSettingName] = useState('');
  const [settingValue, setSettingValue] = useState(''); // used as default in both modes
  const [showValue, setShowValue] = useState(false);
  const [slotSetting, setSlotSetting] = useState(false);

  // ── Per-tenant value mode ──────────────────────────────────
  const [valueMode, setValueMode] = useState('same'); // 'same' | 'perTenant'
  const [tenantValues, setTenantValues] = useState({}); // tenantName → value
  const [editedTenants, setEditedTenants] = useState(new Set()); // manually edited tenants

  // ── Preview state ──────────────────────────────────────────
  const [previewItems, setPreviewItems] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [overrides, setOverrides] = useState({}); // appName → bool

  // ── Apply state ────────────────────────────────────────────
  const [applyResults, setApplyResults] = useState([]);
  const [loadingApply, setLoadingApply] = useState(false);
  const [applyError, setApplyError] = useState(null);

  // ── Derived data ───────────────────────────────────────────
  const tenants = useMemo(() => [...new Set(apps.map((a) => a.tenantName))].sort(), [apps]);
  const serviceTypes = useMemo(() => [...new Set(apps.map((a) => a.serviceType))].sort(), [apps]);

  const filteredApps = useMemo(() => {
    return apps.filter((app) => {
      if (selectedTenants.length > 0 && !selectedTenants.includes(app.tenantName)) return false;
      if (selectedServiceTypes.length > 0 && !selectedServiceTypes.includes(app.serviceType)) return false;
      return true;
    });
  }, [apps, selectedTenants, selectedServiceTypes]);

  const filteredTenants = useMemo(
    () => [...new Set(filteredApps.map((a) => a.tenantName))].sort(),
    [filteredApps]
  );

  // ── Resolve the effective value for a tenant ───────────────
  const getTenantValue = useCallback(
    (tenant) => {
      if (editedTenants.has(tenant) && tenantValues[tenant] !== undefined) {
        return tenantValues[tenant];
      }
      return settingValue; // default value
    },
    [editedTenants, tenantValues, settingValue]
  );

  // ── Build perAppValues dict from tenant mapping ────────────
  const buildPerAppValues = useCallback(() => {
    if (valueMode === 'same') return {};
    const perApp = {};
    filteredApps.forEach((app) => {
      const val = getTenantValue(app.tenantName);
      if (val !== settingValue) {
        perApp[app.name] = val;
      }
    });
    return perApp;
  }, [valueMode, filteredApps, getTenantValue, settingValue]);

  // ── Load apps on mount ─────────────────────────────────────
  useEffect(() => {
    const fetchApps = async () => {
      setLoadingApps(true);
      setAppsError(null);
      try {
        const data = await listApps();
        setApps(data);
      } catch (err) {
        setAppsError(err.message);
      } finally {
        setLoadingApps(false);
      }
    };
    fetchApps();
  }, []);

  // ── Preview handler ────────────────────────────────────────
  const handlePreview = async () => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const targets = filteredApps.map((a) => ({
        appServiceName: a.name,
        resourceGroup: a.resourceGroup,
      }));
      const perAppValues = buildPerAppValues();
      const items = await previewInject({ targets, settingName, settingValue, settingType, perAppValues });
      setPreviewItems(items);
      const defaultOverrides = {};
      items.forEach((item) => {
        if (item.alreadyExists) {
          defaultOverrides[item.appServiceName] = false;
        }
      });
      setOverrides(defaultOverrides);
      setStep(1);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  // ── Apply handler ──────────────────────────────────────────
  const handleApply = async () => {
    setLoadingApply(true);
    setApplyError(null);
    try {
      const targets = previewItems.map((item) => ({
        appServiceName: item.appServiceName,
        resourceGroup: item.resourceGroup,
      }));
      const perAppValues = buildPerAppValues();
      const results = await applyInject({
        targets,
        settingName,
        settingValue,
        slotSetting,
        settingType,
        connectionStringType,
        perAppValues,
        overrides,
      });
      setApplyResults(results);
      setStep(2);
    } catch (err) {
      setApplyError(err.message);
    } finally {
      setLoadingApply(false);
    }
  };

  // ── Reset to step 1 ───────────────────────────────────────
  const handleReset = () => {
    setStep(0);
    setSettingType(0);
    setConnectionStringType(2);
    setSettingName('');
    setSettingValue('');
    setShowValue(false);
    setSlotSetting(false);
    setValueMode('same');
    setTenantValues({});
    setEditedTenants(new Set());
    setPreviewItems([]);
    setOverrides({});
    setApplyResults([]);
    setPreviewError(null);
    setApplyError(null);
  };

  // ── Preview summary ────────────────────────────────────────
  const previewSummary = useMemo(() => {
    const newCount = previewItems.filter((i) => !i.alreadyExists).length;
    const conflictCount = previewItems.filter((i) => i.alreadyExists).length;
    const overrideCount = Object.values(overrides).filter(Boolean).length;
    const willChange = newCount + overrideCount;
    return { newCount, conflictCount, overrideCount, willChange, total: previewItems.length };
  }, [previewItems, overrides]);

  // ── Apply summary ──────────────────────────────────────────
  const applySummary = useMemo(() => {
    const injected = applyResults.filter((r) => r.action === 0).length;
    const overridden = applyResults.filter((r) => r.action === 1).length;
    const skipped = applyResults.filter((r) => r.action === 2).length;
    const failed = applyResults.filter((r) => r.action === 3).length;
    return { injected, overridden, skipped, failed };
  }, [applyResults]);

  // ── Validation ─────────────────────────────────────────────
  const canPreview = useMemo(() => {
    if (!settingName.trim() || filteredApps.length === 0 || loadingPreview) return false;
    if (valueMode === 'same') {
      return settingValue.trim().length > 0;
    }
    // Per-tenant: every filtered tenant must have a non-empty value
    return filteredTenants.every((t) => getTenantValue(t).trim().length > 0);
  }, [settingName, settingValue, filteredApps, filteredTenants, loadingPreview, valueMode, getTenantValue]);

  // ── Step indicator ─────────────────────────────────────────
  const StepIndicator = () => (
    <div className="flex items-center gap-3 mb-6">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            i === step ? 'bg-blue-600 text-white' :
            i < step ? 'bg-green-500 text-white' :
            'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
          }`}>
            {i < step ? '\u2713' : i + 1}
          </div>
          <span className={`text-sm font-medium ${i === step ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-slate-400'}`}>
            {label}
          </span>
          {i < STEPS.length - 1 && <div className="w-8 h-px bg-gray-300 dark:bg-slate-600 mx-1" />}
        </div>
      ))}
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  // STEP 0: CONFIGURE
  // ══════════════════════════════════════════════════════════════
  const renderConfigure = () => (
    <div className="space-y-6">
      {/* App Selection */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Target App Services</h3>

        {loadingApps && (
          <div className="flex items-center gap-3 py-8 justify-center">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-500 dark:text-slate-400">Loading app services...</span>
          </div>
        )}

        {appsError && (
          <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 mb-4">
            {appsError}
          </div>
        )}

        {!loadingApps && !appsError && (
          <>
            <div className="flex flex-wrap gap-4 mb-4">
              <MultiSelectDropdown
                label="Tenants"
                options={tenants}
                selected={selectedTenants}
                onChange={setSelectedTenants}
                disabled={tenants.length === 0}
                placeholder="No tenants found"
              />
              <MultiSelectDropdown
                label="Service Types"
                options={serviceTypes}
                selected={selectedServiceTypes}
                onChange={setSelectedServiceTypes}
                disabled={serviceTypes.length === 0}
                placeholder="No service types found"
              />
            </div>
            <div className="text-sm text-gray-600 dark:text-slate-400">
              <span className="font-semibold text-gray-900 dark:text-white">{filteredApps.length}</span> app service{filteredApps.length !== 1 ? 's' : ''} selected
              {filteredApps.length > 0 && selectedTenants.length === 0 && selectedServiceTypes.length === 0 && (
                <span className="text-yellow-600 dark:text-yellow-400 ml-2">(all — use filters to narrow scope)</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Setting Input */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Setting to Inject</h3>

        {/* Setting Type Tabs */}
        <div className="mb-5">
          <div className="flex border-b border-gray-200 dark:border-slate-700">
            <button
              type="button"
              onClick={() => setSettingType(0)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                settingType === 0
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-500'
              }`}
            >
              App settings
            </button>
            <button
              type="button"
              onClick={() => setSettingType(1)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                settingType === 1
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:border-gray-300 dark:hover:border-slate-500'
              }`}
            >
              Connection strings
            </button>
          </div>
        </div>

        {/* Setting Name */}
        <div className="space-y-1.5 mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {settingType === 0 ? 'Setting Name' : 'Connection String Name'}
          </label>
          <input
            type="text"
            value={settingName}
            onChange={(e) => setSettingName(e.target.value)}
            placeholder={settingType === 0 ? 'e.g. MyApp__FeatureFlag' : 'e.g. DefaultConnection'}
            className="w-full max-w-md px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          />
        </div>

        {/* Connection String Type (only for connection strings) */}
        {settingType === 1 && (
          <div className="space-y-1.5 mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
            <select
              value={connectionStringType}
              onChange={(e) => setConnectionStringType(Number(e.target.value))}
              className="w-full max-w-md px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              {CONN_STRING_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Value Mode Toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Value Mode</label>
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-slate-600 overflow-hidden">
            <button
              type="button"
              onClick={() => setValueMode('same')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                valueMode === 'same'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
              }`}
            >
              Same for all
            </button>
            <button
              type="button"
              onClick={() => setValueMode('perTenant')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 dark:border-slate-600 ${
                valueMode === 'perTenant'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
              }`}
            >
              Per tenant
            </button>
          </div>
        </div>

        {/* Same for all — single value input */}
        {valueMode === 'same' && (
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {settingType === 0 ? 'Setting Value' : 'Connection String Value'}
            </label>
            <div className="relative max-w-md">
              <input
                type={showValue ? 'text' : 'password'}
                value={settingValue}
                onChange={(e) => setSettingValue(e.target.value)}
                placeholder="Enter value"
                className="w-full px-4 py-2.5 pr-16 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowValue(!showValue)}
                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white transition-colors"
              >
                {showValue ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        )}

        {/* Per tenant — default value + tenant table */}
        {valueMode === 'perTenant' && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Default Value <span className="text-gray-400 dark:text-slate-500 font-normal">(pre-fills all tenants)</span></label>
              <div className="relative max-w-md">
                <input
                  type={showValue ? 'text' : 'password'}
                  value={settingValue}
                  onChange={(e) => setSettingValue(e.target.value)}
                  placeholder="Enter default value (optional)"
                  className="w-full px-4 py-2.5 pr-16 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowValue(!showValue)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white transition-colors"
                >
                  {showValue ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {filteredTenants.length > 0 ? (
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-slate-900/50">
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-slate-300 w-48">Tenant</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-slate-300">Value</th>
                      <th className="px-4 py-2.5 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                    {filteredTenants.map((tenant) => {
                      const isEdited = editedTenants.has(tenant);
                      const currentVal = isEdited && tenantValues[tenant] !== undefined ? tenantValues[tenant] : '';
                      return (
                        <tr key={tenant} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                          <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{tenant}</td>
                          <td className="px-4 py-2.5">
                            <input
                              type={showValue ? 'text' : 'password'}
                              value={isEdited ? currentVal : settingValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                setTenantValues((prev) => ({ ...prev, [tenant]: val }));
                                setEditedTenants((prev) => new Set([...prev, tenant]));
                              }}
                              placeholder={settingValue ? `Default: ${showValue ? settingValue : '********'}` : 'Enter value'}
                              className={`w-full px-3 py-1.5 border rounded-lg text-sm transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                isEdited
                                  ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-950/20 text-gray-900 dark:text-gray-100'
                                  : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-500 dark:text-slate-400'
                              }`}
                            />
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {isEdited && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditedTenants((prev) => {
                                    const next = new Set(prev);
                                    next.delete(tenant);
                                    return next;
                                  });
                                  setTenantValues((prev) => {
                                    const next = { ...prev };
                                    delete next[tenant];
                                    return next;
                                  });
                                }}
                                className="text-xs text-gray-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              >
                                Reset
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-slate-400">Select tenants above to configure per-tenant values.</div>
            )}

            {editedTenants.size > 0 && (
              <div className="text-xs text-gray-500 dark:text-slate-400">
                <span className="font-semibold text-blue-600 dark:text-blue-400">{editedTenants.size}</span> tenant{editedTenants.size !== 1 ? 's' : ''} with custom values, {filteredTenants.length - editedTenants.size} using default
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={slotSetting}
              onChange={(e) => setSlotSetting(e.target.checked)}
              className="rounded w-4 h-4"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Mark as deployment slot sticky</span>
          </label>
        </div>
      </div>

      {/* Preview Button */}
      <div className="flex justify-end">
        <button
          onClick={handlePreview}
          disabled={!canPreview}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {loadingPreview && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Preview Injection
        </button>
      </div>

      {previewError && (
        <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {previewError}
        </div>
      )}
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  // STEP 1: PREVIEW
  // ══════════════════════════════════════════════════════════════
  const renderPreview = () => (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex flex-wrap gap-3">
        <Badge color="green">{previewSummary.newCount} New</Badge>
        <Badge color="yellow">{previewSummary.conflictCount} Conflicts</Badge>
        <Badge color="blue">{previewSummary.willChange} Will Change</Badge>
        <Badge color="gray">{previewSummary.total} Total</Badge>
      </div>

      {/* Setting info */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
        <div className="flex flex-wrap gap-6 text-sm">
          <div>
            <Badge color={settingType === 0 ? 'blue' : 'green'}>
              {settingType === 0 ? 'App Setting' : 'Connection String'}
            </Badge>
          </div>
          <div>
            <span className="text-gray-500 dark:text-slate-400">{settingType === 0 ? 'Setting' : 'Name'}: </span>
            <span className="font-mono font-semibold text-gray-900 dark:text-white">{settingName}</span>
          </div>
          {settingType === 1 && (
            <div>
              <span className="text-gray-500 dark:text-slate-400">Type: </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {CONN_STRING_TYPES.find((t) => t.value === connectionStringType)?.label || 'Custom'}
              </span>
            </div>
          )}
          {valueMode === 'same' && (
            <div>
              <span className="text-gray-500 dark:text-slate-400">Value: </span>
              <span className="font-mono text-gray-900 dark:text-white">{showValue ? settingValue : '********'}</span>
              <button onClick={() => setShowValue(!showValue)} className="ml-2 text-xs text-blue-600 dark:text-blue-400 hover:underline">
                {showValue ? 'hide' : 'show'}
              </button>
            </div>
          )}
          {valueMode === 'perTenant' && <Badge color="blue">Per-Tenant Values</Badge>}
          {slotSetting && <Badge color="blue">Slot Sticky</Badge>}
        </div>
      </div>

      {/* Preview table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-900/50">
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">App Service</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Tenant</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Service Type</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Existing Value</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">New Value</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {previewItems.map((item) => (
                <tr key={item.appServiceName} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-900 dark:text-white">{item.appServiceName}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-slate-400">{item.tenantName}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-slate-400">{item.serviceType}</td>
                  <td className="px-4 py-3">
                    {item.alreadyExists ? <Badge color="yellow">EXISTS</Badge> : <Badge color="green">NEW</Badge>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-slate-400">
                    {item.alreadyExists ? item.existingValue : '\u2014'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-blue-700 dark:text-blue-400">
                    {showValue ? item.newValue : '********'}
                  </td>
                  <td className="px-4 py-3">
                    {item.alreadyExists ? (
                      <select
                        value={overrides[item.appServiceName] ? 'override' : 'keep'}
                        onChange={(e) => setOverrides((prev) => ({
                          ...prev,
                          [item.appServiceName]: e.target.value === 'override',
                        }))}
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300"
                      >
                        <option value="keep">Keep existing</option>
                        <option value="override">Override</option>
                      </select>
                    ) : (
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">Will inject</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <button
          onClick={() => setStep(0)}
          className="px-5 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleApply}
          disabled={previewSummary.willChange === 0 || loadingApply}
          className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {loadingApply && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Apply to {previewSummary.willChange} App{previewSummary.willChange !== 1 ? 's' : ''}
        </button>
      </div>

      {applyError && (
        <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {applyError}
        </div>
      )}
    </div>
  );

  // ══════════════════════════════════════════════════════════════
  // STEP 2: RESULTS
  // ══════════════════════════════════════════════════════════════
  const renderResults = () => {
    const actionBadge = (action) => {
      switch (action) {
        case 0: return <Badge color="green">Injected</Badge>;
        case 1: return <Badge color="blue">Overridden</Badge>;
        case 2: return <Badge color="gray">Skipped</Badge>;
        case 3: return <Badge color="red">Failed</Badge>;
        default: return <Badge color="gray">Unknown</Badge>;
      }
    };

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="flex flex-wrap gap-3">
          {applySummary.injected > 0 && <Badge color="green">{applySummary.injected} Injected</Badge>}
          {applySummary.overridden > 0 && <Badge color="blue">{applySummary.overridden} Overridden</Badge>}
          {applySummary.skipped > 0 && <Badge color="gray">{applySummary.skipped} Skipped</Badge>}
          {applySummary.failed > 0 && <Badge color="red">{applySummary.failed} Failed</Badge>}
        </div>

        {/* Results table */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">App Service</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Action</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-slate-300">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {applyResults.map((result) => (
                  <tr key={result.appServiceName} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-900 dark:text-white">{result.appServiceName}</td>
                    <td className="px-4 py-3">{actionBadge(result.action)}</td>
                    <td className="px-4 py-3">
                      {result.success
                        ? <span className="text-green-600 dark:text-green-400 font-medium text-xs">Success</span>
                        : <span className="text-red-600 dark:text-red-400 font-medium text-xs">Failed</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-400">{result.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reset */}
        <div className="flex justify-end">
          <button
            onClick={handleReset}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
          >
            Inject Another
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <StepIndicator />
      {step === 0 && renderConfigure()}
      {step === 1 && renderPreview()}
      {step === 2 && renderResults()}
    </div>
  );
};

export default EnvInjector;
